import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, getStoredToken } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Check, Loader2, Lock, Gift } from "lucide-react";
import { toast } from "sonner";
import TrustLine from "../components/TrustLine";

// TEMPORARY workaround — Emergent platform is truncating STRIPE_API_KEY env
// var on production deploy (107 chars in → 46 chars stored), so the API
// checkout endpoint fails. Until support fixes the env truncation, route
// users straight to Stripe-hosted Payment Links (no API key needed at our
// server). These links carry the 3-day trial. Promo codes (TL888, Family888)
// are NOT available on Payment Links — re-enable api flow once env is fixed.
const STRIPE_PAYMENT_LINKS = {
  basic:           "https://buy.stripe.com/aFa28sgxsdqv9If71w28803",
  basic_annual:    "https://buy.stripe.com/fZu3cw4OK4TZf2zdpU28804",
  advanced:        "https://buy.stripe.com/5kQeVe95086b07F2Lg28805",
  advanced_annual: "https://buy.stripe.com/eVqeVe1Cy2LR7A73Pk28806",
};

export default function PricingPage() {
  const { user, loading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [interval, setInterval] = useState("annual"); // default to annual (Best Value)
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/billing/plans`);
        setPlans(r.data.plans || []);
      } catch (e) {}
    })();
  }, []);

  // group plans by family (basic/advanced) and interval
  const byKey = useMemo(() => {
    const m = {};
    plans.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [plans]);

  const subscribe = async (plan_id) => {
    // Guard: signup first (so trial state, profile, and Stripe email match).
    if (!user) {
      sessionStorage.setItem("ascend_pending_plan", plan_id);
      navigate("/start");
      return;
    }

    setBusy(plan_id);

    // Best-effort analytics ping before redirect (non-blocking)
    axios
      .post(`${API}/analytics/event`, {
        event: "checkout_clicked",
        properties: { plan_id, via: "payment_link" },
      })
      .catch(() => {});

    // Redirect straight to Stripe-hosted Payment Link.
    // These four URLs are the ONLY active checkout destinations — pre-fill
    // email + tag the session with client_reference_id so the eventual
    // webhook can match the Stripe customer back to our user record.
    const link = STRIPE_PAYMENT_LINKS[plan_id];
    if (!link) {
      // Should never happen — all 4 known plan_ids have a link. If a new
      // plan_id slipped through, show a recoverable message (NOT the scary
      // "Payments temporarily unavailable" toast).
      toast.error("This plan link is being updated. Please refresh the page.");
      setBusy(null);
      return;
    }
    try {
      const url = new URL(link);
      if (user?.email) url.searchParams.set("prefilled_email", user.email);
      url.searchParams.set("client_reference_id", user.user_id || "");
      // Stash the plan the user is about to pay for so BillingSuccessPage
      // can optimistically grant it on return (Payment Links don't call our
      // server directly). Cleared after successful claim.
      sessionStorage.setItem("ascend_last_checkout_plan", plan_id);
      window.location.href = url.toString();
    } catch (e) {
      // URL parse failed — fall back to the bare link rather than blocking.
      sessionStorage.setItem("ascend_last_checkout_plan", plan_id);
      window.location.href = link;
    }
  };

  useEffect(() => {
    if (loading || !user) return;
    const pending = sessionStorage.getItem("ascend_pending_plan");
    if (pending) {
      sessionStorage.removeItem("ascend_pending_plan");
      subscribe(pending);
    }
    // eslint-disable-next-line
  }, [loading, user]);

  const redeem = async () => {
    const code = redeemCode.trim();
    if (!code) return toast.error("Enter a code first.");
    if (!user) {
      // Logged-out — route to signup so the code is applied there.
      sessionStorage.setItem("ascend_pending_code", code);
      navigate("/start");
      return;
    }
    setRedeeming(true);
    try {
      const r = await axios.post(`${API}/billing/redeem`, { code });
      toast.success(r.data.message || "Code redeemed!", { duration: 6000 });
      setRedeemCode("");
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "That code didn't work. Try again.");
    } finally {
      setRedeeming(false);
    }
  };

  const FEATURES = {
    basic: [
      "Personalized AI life roadmap",
      "5-pillar life structure (Financial, Health, Spirituality, Relationships, Endgame)",
      "Daily goals and habit guidance",
      "Daily check-ins",
      "Progress tracking and reflection tools",
      "Personal growth prompts",
      "Awareness & clarity practices library",
    ],
    advanced: [
      "Everything in Basic",
      "Voice coaching — talk to your coach like a friend on the phone",
      "Persistent memory across all sessions",
      "Weekly insights and pattern read",
      "Coach can adjust your roadmap mid-chat",
      "Priority access to new features",
    ],
  };

  const basic = interval === "annual" ? byKey.basic_annual : byKey.basic;
  const advanced = interval === "annual" ? byKey.advanced_annual : byKey.advanced;

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="px-6 py-6 max-w-7xl mx-auto flex items-center justify-between">
        <Link
          to={user || getStoredToken() ? "/dashboard" : "/"}
          data-testid="pricing-back-link"
          className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]"
        >
          <ArrowLeft size={16} /> {user || getStoredToken() ? "Dashboard" : "Back"}
        </Link>
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 pb-20">
        <div className="text-center mb-10">
          <p className="font-mono-eyebrow text-[#486a8f] mb-3">Pricing</p>
          <h1 className="font-display text-[40px] md:text-[56px] leading-[1.05] text-[#0f1724] tracking-tight">
            Keep your roadmap{" "}
            <span className="italic font-light text-[#486a8f]">alive</span>.
          </h1>
          <p className="font-body text-[15px] md:text-[16px] text-[#4b5563] mt-4 max-w-xl mx-auto leading-relaxed">
            Your coach remembers your goals, checks in daily, and keeps adjusting your plan as your life changes.
          </p>
        </div>

        {/* Interval toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex p-1 rounded-full bg-white border border-[#eadfc9]">
            <button
              onClick={() => setInterval("monthly")}
              className={`px-5 py-2 rounded-full font-mono-eyebrow text-[11px] transition-all ${
                interval === "monthly"
                  ? "bg-[#486a8f] text-white"
                  : "text-[#486a8f] hover:bg-[#faf6ec]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("annual")}
              className={`px-5 py-2 rounded-full font-mono-eyebrow text-[11px] transition-all ${
                interval === "annual"
                  ? "bg-[#486a8f] text-white"
                  : "text-[#486a8f] hover:bg-[#faf6ec]"
              }`}
            >
              Annual · Save with annual billing
            </button>
          </div>
        </div>

        {/* Reassurance — Basic mentor guidance is included; Advanced unlocks deeper features. */}
        <p
          data-testid="pricing-basic-included-line"
          className="text-center font-body text-[13px] text-[#4b5563] -mt-4 mb-8 max-w-md mx-auto leading-relaxed"
        >
          Basic includes daily roadmap + AI mentor guidance.{" "}
          <span className="text-[#0f1724] font-medium">Advanced</span> adds voice coaching,
          persistent memory, deeper personalization, and weekly insights.
        </p>

        {/* REDEEM CODE — handed out to family / friends / beta users */}
        <div
          data-testid="pricing-redeem-box"
          className="max-w-md mx-auto mb-10 rounded-2xl bg-white border border-[#eadfc9] p-5 shadow-[0_10px_40px_-20px_rgba(15,23,36,0.15)]"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#f3ecd9] flex items-center justify-center shrink-0">
              <Gift size={16} className="text-[#486a8f]" />
            </div>
            <div>
              <p className="font-display text-[16px] font-semibold text-[#0f1724] leading-tight">
                Have a code?
              </p>
              <p className="font-body text-[12.5px] text-[#4b5563] leading-relaxed mt-0.5">
                Redeem it to unlock access instantly — no card required.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="pricing-redeem-input"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="ENTER YOUR CODE"
              className="h-11 uppercase tracking-wide"
              onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
            />
            <Button
              data-testid="pricing-redeem-btn"
              onClick={redeem}
              disabled={redeeming || !redeemCode.trim()}
              className="h-11 px-5 rounded-md bg-[#3e6b52] hover:bg-[#2f5440] text-white font-medium text-[13px] whitespace-nowrap"
            >
              {redeeming ? <Loader2 size={14} className="animate-spin" /> : "Redeem"}
            </Button>
          </div>
        </div>

        {(!basic || !advanced) ? (
          <div className="text-center py-20">
            <Loader2 size={28} className="mx-auto text-[#486a8f] animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {/* BASIC */}
            <PlanCard
              plan={basic}
              interval={interval}
              features={FEATURES.basic}
              tagline="Daily roadmap + AI mentor guidance"
              accent="#486a8f"
              accentBg="#e8eef6"
              onBuy={() => subscribe(basic.id)}
              busy={busy === basic.id}
              badge={null}
            />
            {/* ADVANCED */}
            <PlanCard
              plan={advanced}
              interval={interval}
              features={FEATURES.advanced}
              tagline="Adds voice, deeper memory, deeper personalization & weekly insights"
              accent="#3e6b52"
              accentBg="#e9f0ec"
              onBuy={() => subscribe(advanced.id)}
              busy={busy === advanced.id}
              badge={interval === "annual" ? "BEST VALUE" : "MOST POWERFUL"}
              elevated
            />
          </div>
        )}

        {/* Trust line */}
        <p className="text-center font-body text-[14px] text-[#4b5563] mt-10">
          Cancel anytime. Built for personal growth, organization, reflection, and accountability.
        </p>
        <div className="flex justify-center mt-4">
          <TrustLine />
        </div>

        {/* Honest comparison anchor */}
        <div className="mt-8 max-w-2xl mx-auto rounded-2xl bg-white border border-[#eadfc9] p-6 text-center">
          <p className="font-body text-[14px] text-[#4b5563] leading-relaxed">
            Traditional coaching can cost hundreds per month.{" "}
            <span className="text-[#0f1724] font-medium">Ascend gives you daily AI-guided structure starting at $8.88/month.</span>{" "}
            A daily roadmap for your goals, habits, and next steps — built to help you feel less scattered and more intentional.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 max-w-3xl mx-auto">
          <p className="font-body text-[12px] text-[#6b7280] leading-relaxed text-center">
            Ascend Higher is for personal growth, organization, reflection, and habit-building. It is not therapy,
            medical care, financial advice, legal advice, crisis support, or a replacement for licensed professionals.
            Results depend on your effort and circumstances; outcomes are not guaranteed. If you are in crisis, please
            contact a licensed professional or call/text{" "}
            <a href="tel:988" className="text-[#486a8f] font-medium hover:underline">988</a> (US).
          </p>
        </div>
      </main>
    </div>
  );
}

function PlanCard({ plan, interval, features, tagline, accent, accentBg, onBuy, busy, badge, elevated, elite }) {
  // accurate effective monthly for annual
  const monthlyEquivalent = interval === "annual" ? (plan.amount / 12).toFixed(2) : null;

  // Elite "Black Card" aesthetic overrides
  const cardClass = elite
    ? "relative rounded-3xl p-10 md:p-12 border border-[#c9a14a]/40 shadow-[0_20px_60px_-20px_rgba(201,161,74,0.25)] bg-[#0e0f14] text-[#e6dcc6]"
    : `relative rounded-3xl bg-white p-10 md:p-12 border ${elevated ? "border-[#3e6b52]/40 shadow-[0_20px_60px_-30px_rgba(62,107,82,0.35)]" : "border-[#eadfc9]"}`;

  const priceColor = elite ? "text-[#f5ecd0]" : "text-[#0f1724]";
  const mutedText = elite ? "text-[#c9a14a]/90" : "text-[#6b7280]";
  const featureText = elite ? "text-[#e6dcc6]" : "text-[#2b3442]";
  const badgeBg = elite ? "#c9a14a" : elevated ? "#3e6b52" : "#486a8f";
  const ctaBg = elite ? "#c9a14a" : elevated ? "#3e6b52" : "#486a8f";
  const ctaText = elite ? "text-[#0e0f14]" : "text-white";

  return (
    <div className={cardClass}>
      {badge && (
        <span
          className={`absolute -top-3 left-10 px-4 py-1.5 rounded-full font-mono-eyebrow text-[10px] ${elite ? "text-[#0e0f14]" : "text-white"}`}
          style={{ background: badgeBg }}
        >
          {badge}
        </span>
      )}

      <p className="font-mono-eyebrow mb-2" style={{ color: accent }}>
        {plan.name.toUpperCase()}
      </p>
      <p className={`font-body text-[13px] ${mutedText} mb-5`}>{tagline}</p>

      <div className="mb-2 flex items-end gap-1">
        <span className={`font-display text-[58px] md:text-[68px] leading-none ${priceColor} tracking-tight`}>
          ${plan.amount}
        </span>
        <span className={`font-body ${mutedText} mb-3 text-[15px]`}>
          /{interval === "annual" ? "year" : "month"}
        </span>
      </div>
      {interval === "annual" ? (
        <p className={`font-body text-[13px] ${mutedText} mb-7`}>
          About <span className={`${priceColor} font-medium`}>${monthlyEquivalent}/month</span>, billed annually.
        </p>
      ) : (
        <p className={`font-body text-[13px] ${mutedText} mb-7`}>Billed monthly.</p>
      )}

      <ul className="space-y-3 mb-8">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <span
              className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
              style={{ background: accentBg, color: accent }}
            >
              <Check size={12} strokeWidth={3} />
            </span>
            <span className={`font-body text-[14px] ${featureText} leading-relaxed`}>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        data-testid={`pricing-buy-${plan.id}-btn`}
        onClick={onBuy}
        disabled={busy}
        className={`w-full h-12 rounded-full font-medium text-[15px] btn-lift ${ctaText}`}
        style={{ background: ctaBg }}
      >
        {busy ? (
          <>
            <Loader2 size={15} className="mr-2 animate-spin" /> Loading…
          </>
        ) : (
          <>{elite ? "Enter the Titan tier" : "Start 3-Day Free Trial"}</>
        )}
      </Button>
      <div className={`flex items-center justify-center gap-2 mt-4 ${mutedText}`}>
        <Lock size={11} />
        <p className="font-body text-[12px]">Card required · Cancel anytime · Secure checkout by Stripe</p>
      </div>
      <p className={`font-body text-[11px] ${elite ? "text-[#c9a14a]/60" : "text-[#9aa3ae]"} text-center mt-2`}>
        No charge today. {interval === "annual" ? `Then $${plan.amount}/year` : `Then $${plan.amount}/month`} after the 3-day trial — only if you stay.
      </p>
    </div>
  );
}
